import type { Language } from '../ui/i18n';
import type { Townsperson } from './types';
import { CITY_SOURCES } from '../history/sources';
import { careReplies } from './careDialogue';

type Text = readonly [english: string, dutch: string];
export interface DialogueReply {
  readonly id: string;
  readonly label: string;
  readonly text: string;
  readonly bubble: string;
  readonly source: string;
}
const text = (value: Text, language: Language): string => value[language === 'nl' ? 1 : 0];
const fishTrade = (trade: string): boolean =>
  ['fishwife', 'fishmonger', 'fisherman'].includes(trade);
const religious = (trade: string): boolean => ['friar', 'sister'].includes(trade);
const guard = (trade: string): boolean => ['watchman', 'schout'].includes(trade);

const TRADES: Readonly<Record<string, Text>> = {
  fishwife: ['Fish seller', 'Visverkoopster'],
  fishmonger: ['Fish seller', 'Visverkoper'],
  fisherman: ['Fisherman', 'Visser'],
  friar: ['Friar', 'Broeder'],
  sister: ['Sister', 'Zuster'],
  watchman: ['Watchman', 'Wachter'],
  schout: ['Schout', 'Schout'],
  merchant: ['Merchant', 'Koopman'],
  market_woman: ['Market trader', 'Marktvrouw'],
  baker: ['Baker', 'Bakker'],
  bakers_wife: ['Baker', 'Bakkersvrouw'],
  butcher: ['Butcher', 'Slager'],
  carpenter: ['Carpenter', 'Timmerman'],
  cooper: ['Cooper', 'Kuiper'],
  smith: ['Smith', 'Smid'],
  mason: ['Mason', 'Metselaar'],
  shoemaker: ['Shoemaker', 'Schoenmaker'],
  weaver: ['Weaver', 'Wever'],
  sailor: ['Sailor', 'Zeeman'],
  shipmaster: ['Shipmaster', 'Schipper'],
  burgomaster: ['Burgomaster', 'Burgemeester'],
  gentlewoman: ['Townswoman', 'Stadsvrouw'],
  musician: ['Musician', 'Muzikant'],
  carer: ['Gasthuis attendant', 'Gasthuismedewerker'],
  leprozen_carer: ['Leprozenhuis attendant', 'Medewerker van het Leprozenhuis'],
  physician: ['Personal physician', 'Persoonlijke arts'],
};

export function tradeLabel(person: Townsperson, language: Language): string {
  return text(TRADES[person.trade] ?? ['Townsperson', 'Stadsgenoot'], language);
}

export function townGreeting(person: Townsperson, visitor: string, language: Language): string {
  if (['carer', 'leprozen_carer', 'physician'].includes(person.trade)) {
    const greeting: Text =
      person.trade === 'physician'
        ? [
            'Good day, {name}. I am visiting patients at home. What would you like to know?',
            'Goedendag, {name}. Ik bezoek patiënten thuis. Wat wil je weten?',
          ]
        : [
            'Welcome, {name}. There is bread to bring and linen to sort. Curious about life in the gasthuis?',
            'Welkom, {name}. Er moet brood worden gebracht en linnen gesorteerd. Benieuwd naar het leven in het gasthuis?',
          ];
    return text(greeting, language).replace('{name}', visitor);
  }
  const greeting: Text = fishTrade(person.trade)
    ? [
        'Good day, {name}! The boats are bringing in fish. What would you like to know?',
        'Goedendag, {name}! De boten brengen vis binnen. Wat wil je weten?',
      ]
    : religious(person.trade)
      ? [
          'Peace be with you, {name}. Have you spotted our church towers yet?',
          'Vrede zij met je, {name}. Heb je onze kerktorens al gevonden?',
        ]
      : guard(person.trade)
        ? [
            'Well met, {name}! Keep your light close after dark. Curious about the gates?',
            'Gegroet, {name}! Houd je licht bij je als het donker wordt. Nieuwsgierig naar de poorten?',
          ]
        : [
            'Good day, {name}! Have you seen the boats and market stalls? There is a whole city to discover.',
            'Goedendag, {name}! Heb je de boten en marktkramen gezien? Er is een hele stad te ontdekken.',
          ];
  return text(greeting, language).replace('{name}', visitor);
}

export function townCall(person: Townsperson, language: Language): string {
  if (person.trade === 'physician')
    return text(['On my way to a house visit.', 'Op weg naar een huisbezoek.'], language);
  if (person.trade === 'carer' || person.trade === 'leprozen_carer')
    return text(
      ['Bread and linen for the gasthuis.', 'Brood en linnen voor het gasthuis.'],
      language
    );
  return text(
    fishTrade(person.trade)
      ? ['Looking for fish? Come to the Dam!', 'Vis nodig? Kom naar de Dam!']
      : religious(person.trade)
        ? ['Peace be with you, traveller.', 'Vrede zij met je, reiziger.']
        : guard(person.trade)
          ? ['Mind the water, traveller.', 'Pas op bij het water, reiziger.']
          : ['Good day, traveller.', 'Goedendag, reiziger.'],
    language
  );
}

export function townReplies(person: Townsperson, language: Language): readonly DialogueReply[] {
  return careReplies(person.trade, language) ?? cityReplies(person, language);
}

function cityReplies(person: Townsperson, language: Language): readonly DialogueReply[] {
  const fish = fishTrade(person.trade);
  const first: DialogueReply = {
    id: 'neighbourhood',
    label: text(
      fish
        ? ['Why does the fish arrive by boat?', 'Waarom komt de vis met de boot?']
        : religious(person.trade)
          ? ['How do I find the churches?', 'Hoe vind ik de kerken?']
          : guard(person.trade)
            ? ['What if I arrive after dark?', 'Wat als ik na donker aankom?']
            : ['Where is everyone taking their goods?', 'Waar brengt iedereen de spullen heen?'],
      language
    ),
    text: text(
      fish
        ? [
            'The Damrak reaches right up to the fish market on the Dam. Boats bring the catch straight to the covered benches. Look how close the water and the market are!',
            'Het Damrak komt helemaal tot aan de vismarkt op de Dam. Boten brengen de vangst zo naar de overdekte banken. Kijk eens hoe dicht het water bij de markt ligt!',
          ]
        : religious(person.trade)
          ? [
              'Look above the rooftops for the Oude Kerk and Nieuwe Kerk. Inside our walls you will also find convents. In this Catholic city, they take up quite a lot of room!',
              'Kijk boven de daken uit naar de Oude Kerk en Nieuwe Kerk. Binnen de muren vind je ook kloosters. In onze katholieke stad nemen die heel wat ruimte in!',
            ]
          : guard(person.trade)
            ? [
                'The gates are locked at night. The keys go to the city government at the Stadhuis on the Dam. Find the gates before sunset: every entrance has to be watched.',
                'De poorten gaan ’s nachts op slot. De sleutels gaan naar het stadsbestuur in het Stadhuis op de Dam. Zoek de poorten eens voor zonsondergang: elke ingang moet bewaakt worden.',
              ]
            : [
                'Many goods come through the Dam, our busy square beside the Damrak. Look for the boats, the fish market and the Stadhuis. Water and trading meet in one place!',
                'Veel spullen komen langs de Dam, ons drukke plein naast het Damrak. Zoek de boten, de vismarkt en het Stadhuis. Water en handel komen hier samen!',
              ],
      language
    ),
    source: fish ? CITY_SOURCES.fish.url : CITY_SOURCES.map.url,
    bubble: text(
      fish
        ? ['The boats bring fish straight to the Dam.', 'De boten brengen vis zo naar de Dam.']
        : religious(person.trade)
          ? ['Look above the roofs for our church towers.', 'Zoek onze kerktorens boven de daken.']
          : guard(person.trade)
            ? ['The gates are locked at night.', 'De poorten gaan ’s nachts op slot.']
            : ['Follow the boats and trade to the Dam!', 'Volg de boten en handel naar de Dam!'],
      language
    ),
  };
  return [first, cityReply(language)];
}

function cityReply(language: Language): DialogueReply {
  return {
    id: 'city',
    label: text(['How can I find my way?', 'Hoe vind ik de weg?'], language),
    text: text(
      [
        'Find the Damrak and follow it towards the Dam. The city walls and canals surround our town. Try spotting the Singel, Geldersekade and Kloveniersburgwal on your map.',
        'Zoek het Damrak en volg het naar de Dam. De stadsmuren en grachten liggen rondom onze stad. Kun je de Singel, Geldersekade en Kloveniersburgwal op je kaart vinden?',
      ],
      language
    ),
    source: CITY_SOURCES.map.url,
    bubble: text(['Find the Damrak on your map.', 'Zoek het Damrak op je kaart.'], language),
  };
}
